import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Image as ImageIcon, Minus, Plus, ArrowLeft } from 'lucide-react';
import { useShopCart } from '../../context/ShopCartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n);

export default function ShopProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useShopCart();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/shop/catalog/${id}`).then(r => setProduct(r.data)).catch(() => setProduct(null));
  }, [id]);

  if (!product) return <div className="max-w-3xl mx-auto p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-primary mb-4 inline-flex items-center gap-1" data-testid="back-link"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="aspect-square rounded-xl bg-gradient-to-br from-muted to-muted/40 flex items-center justify-center overflow-hidden">
          {product.image_url ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" /> : <ImageIcon className="w-20 h-20 text-muted-foreground/50" />}
        </div>
        <div>
          <div className="text-sm text-muted-foreground capitalize">{product.category}</div>
          <h1 className="text-3xl font-serif font-bold mt-1" data-testid="product-detail-name">{product.name}</h1>
          <div className="mt-4">
            {product.discount_percent > 0 && (
              <div className="text-sm text-muted-foreground line-through">RWF {formatRWF(product.price)}</div>
            )}
            <div className="text-3xl font-bold text-primary">RWF {formatRWF(product.final_price)} <span className="text-base font-normal text-muted-foreground">/ {product.unit}</span></div>
          </div>
          {product.short_description && <p className="text-sm text-muted-foreground mt-4">{product.short_description}</p>}
          <div className="mt-6">
            {product.in_stock ? (
              <div className="text-sm text-green-700 font-medium" data-testid="product-stock-status">In stock — {product.stock} {product.unit} available</div>
            ) : (
              <div className="text-sm text-red-600 font-medium" data-testid="product-stock-status">Out of stock</div>
            )}
          </div>
          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center border border-border rounded-md">
              <Button variant="ghost" size="sm" onClick={() => setQty(q => Math.max(1, q - 1))} data-testid="qty-decrease"><Minus className="w-4 h-4" /></Button>
              <Input type="number" min="1" max={product.stock} value={qty} onChange={(e) => setQty(Math.max(1, parseFloat(e.target.value) || 1))} className="w-16 text-center border-0" data-testid="qty-input" />
              <Button variant="ghost" size="sm" onClick={() => setQty(q => Math.min(product.stock, q + 1))} data-testid="qty-increase"><Plus className="w-4 h-4" /></Button>
            </div>
            <Button size="lg" disabled={!product.in_stock} onClick={() => { addItem(product, qty); toast.success(`Added ${qty} ${product.unit} of ${product.name}`); }} data-testid="add-to-cart-detail">
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
